// Bilingual dictionary for short shared labels (nav, buttons, row labels, etc.).
// One-off copy is handled inline via the `tr(en, de)` helper from useLang().
//
// Fallback chain in useLang().t(): de -> en -> key.

export const translations = {
  // ── Navigation ───────────────────────────────────────────────────────────
  'nav.work': { en: 'Work', de: 'Projekte' },
  'nav.services': { en: 'Services', de: 'Leistungen' },
  'nav.ai': { en: 'AI', de: 'KI' },
  'nav.about': { en: 'About', de: 'Über uns' },
  'nav.faq': { en: 'FAQ', de: 'FAQ' },
  'nav.contact': { en: 'Contact Us', de: 'Kontakt' },
  'nav.cta': { en: "Let's Talk", de: 'Sprechen wir' },
  'nav.openMenu': { en: 'Open menu', de: 'Menü öffnen' },
  'nav.closeMenu': { en: 'Close menu', de: 'Menü schließen' },

  // ── Hero ─────────────────────────────────────────────────────────────────
  'hero.scroll': { en: 'Scroll', de: 'Scrollen' },
  'hero.scrollAria': { en: 'Scroll to our work', de: 'Zu unseren Projekten scrollen' },

  // ── Clients ──────────────────────────────────────────────────────────────
  'clients.eyebrow': { en: 'Trusted by', de: 'Vertrauen von' },
  'clients.since': { en: 'Since', de: 'Seit' },

  // ── Work rows (NETFLIX_ROWS labels + On-location Delivery tag) ───────────
  // Keyed by the English category string so the data file stays untouched.
  'row.Selected Work': { en: 'Selected Work', de: 'Ausgewählte Projekte' },
  'row.Promos and Highlights': { en: 'Promos and Highlights', de: 'Promos & Highlights' },
  'row.Brand & Live': { en: 'Brand & Live', de: 'Markenfilm & Live' },
  'row.Booth & Activation': { en: 'Booth & Activation', de: 'Stand & Aktivierung' },
  'row.Small Events': { en: 'Small Events', de: 'Kleine Events' },
  'row.On-location Delivery': { en: 'On-location Delivery', de: 'Vor-Ort-Zustellung' },

  // ── Services ─────────────────────────────────────────────────────────────
  'services.eyebrow': { en: 'What We Do', de: 'Unsere Leistungen' },
  'services.watchExample': { en: 'Watch example', de: 'Beispiel ansehen' },
  'services.onRequest': { en: '⚡ On Request', de: '⚡ auf Anfrage' },

  // ── AI ───────────────────────────────────────────────────────────────────
  'ai.eyebrow': { en: 'On AI', de: 'Zur KI' },

  // ── About ────────────────────────────────────────────────────────────────
  'about.eyebrow': { en: 'About', de: 'Über uns' },

  // ── Testimonials ─────────────────────────────────────────────────────────
  'testimonials.eyebrow': { en: 'What clients say', de: 'Was Kund:innen sagen' },

  // ── Locations ────────────────────────────────────────────────────────────
  'locations.eyebrow': { en: 'On the Road', de: 'Unterwegs' },

  // ── FAQ ──────────────────────────────────────────────────────────────────
  'faq.eyebrow': { en: 'Good to Know', de: 'Gut zu wissen' },

  // ── Contact ──────────────────────────────────────────────────────────────
  'contact.eyebrow': { en: 'Contact', de: 'Kontakt' },
  'contact.name': { en: 'Name', de: 'Name' },
  'contact.company': { en: 'Company / Organisation', de: 'Unternehmen / Organisation' },
  'contact.email': { en: 'Email', de: 'E-Mail' },
  'contact.message': { en: 'What are you working on?', de: 'Woran arbeiten Sie?' },
  'contact.send': { en: 'Send Message', de: 'Nachricht senden' },
  'contact.sending': { en: 'Sending…', de: 'Wird gesendet…' },

  // ── Footer ───────────────────────────────────────────────────────────────
  'footer.startProject': { en: 'Start a project →', de: 'Projekt starten →' },
  'footer.impressum': { en: 'Impressum', de: 'Impressum' },
  'footer.datenschutz': { en: 'Datenschutz', de: 'Datenschutz' },

  // ── Case study ───────────────────────────────────────────────────────────
  'caseStudy.back': { en: '← Back to work', de: '← Zurück zu den Projekten' },
  'caseStudy.backHome': { en: '← Back to home', de: '← Zurück zur Startseite' },
  'caseStudy.next': { en: 'Next project →', de: 'Nächstes Projekt →' },
  'caseStudy.campaign': { en: 'Campaign', de: 'Kampagne' },
  'caseStudy.caseStudy': { en: 'Case study', de: 'Fallstudie' },
  'caseStudy.outcomeLabel': { en: 'Outcome · ', de: 'Ergebnis · ' },
  'caseStudy.filmsComingSoon': { en: 'Films coming soon.', de: 'Filme folgen in Kürze.' },
  'caseStudy.videoComingSoon': { en: 'Video coming soon.', de: 'Video folgt in Kürze.' },
  'caseStudy.loadingPlayer': { en: 'Loading player…', de: 'Player wird geladen…' },
  'caseStudy.notFound': { en: 'Project not found', de: 'Projekt nicht gefunden' },
  'caseStudy.notFoundBody': {
    en: "We couldn't find that case study. It may have been moved or renamed.",
    de: 'Diese Fallstudie konnten wir nicht finden. Vielleicht wurde sie verschoben oder umbenannt.',
  },

  // ── Legal back links ─────────────────────────────────────────────────────
  'legal.backHome': { en: '← Back to home', de: '← Zurück zur Startseite' },
  'legal.home': { en: '← Home', de: '← Start' },
  'legal.datenschutzNext': { en: 'Datenschutz →', de: 'Datenschutz →' },
  'legal.impressumNext': { en: 'Impressum →', de: 'Impressum →' },

  // ── NotFound ─────────────────────────────────────────────────────────────
  'notfound.heading': { en: 'PAGE NOT FOUND', de: 'SEITE NICHT GEFUNDEN' },
  'notfound.cta': { en: 'Back to Home', de: 'Zurück zur Startseite' },

  // ── BackToTop / video chrome ─────────────────────────────────────────────
  'backToTop.aria': { en: 'Back to top', de: 'Nach oben' },
  'video.close': { en: 'Close', de: 'Schließen' },
  'video.loadingPlayer': { en: 'Loading player…', de: 'Player wird geladen…' },
  'video.exampleComingSoon': { en: 'Example video coming soon.', de: 'Beispielvideo folgt in Kürze.' },

  // ── Badges ───────────────────────────────────────────────────────────────
  'badge.Series': { en: 'Series', de: 'Serie' },
  'badge.Campaign': { en: 'Campaign', de: 'Kampagne' },
};
